import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ClipboardCheck, 
  Activity, 
  ShieldCheck, 
  Zap, 
  Database, 
  Stethoscope 
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ConstitutionCard, FeatureCard } from '@/components/Cards';
import { 
  ROUTE_PATHS, 
  CONSTITUTION_TYPES, 
  ConstitutionInfo 
} from '@/lib/index';
import { IMAGES } from '@/assets/images';

const springPresets = {
  gentle: { stiffness: 300, damping: 35 },
  smooth: { stiffness: 200, damping: 40 },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Home: React.FC = () => {
  const constitutions = Object.values(CONSTITUTION_TYPES);

  return (
    <Layout>
      {/* Hero Section */}
      <section id="hero" className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.KOREAN_MEDICINE_8} 
            alt="Korean Medicine Heritage" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span 
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="inline-block px-4 py-1.5 mb-6 text-sm font-medium tracking-wider text-primary bg-primary/10 rounded-full border border-primary/20"
            >
              2026 NEXT-GEN WELLNESS SYSTEM
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="text-5xl md:text-7xl font-bold leading-tight mb-6"
            >
              당신의 체질에 맞는<br />
              <span className="text-primary">한방 단백질</span>의 정밀한 관리
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed"
            >
              사상체질 이론과 현대적 데이터 분석을 결합하여, 단순한 영양 공급을 넘어 
              개인의 생리적 특성에 최적화된 지속 가능한 건강 관리 솔루션을 제공합니다.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="flex flex-wrap gap-4"
            >
              <Link 
                to={ROUTE_PATHS.DIAGNOSIS}
                className="px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:scale-[1.02] active:scale-[0.97] transition-all flex items-center gap-2 shadow-lg shadow-primary/20"
              >
                무료 체질 진단 시작하기 <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to={ROUTE_PATHS.ABOUT}
                className="px-8 py-4 bg-secondary text-secondary-foreground rounded-full font-semibold hover:bg-secondary/80 transition-all border border-border"
              >
                시스템 더 알아보기
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About System Section */}
      <section id="about" className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.smooth}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                사상체질 이론 기반의<br />
                구조적 관리 시스템
              </h2>
              <div className="space-y-6 text-muted-foreground">
                <p className="text-lg">
                  본 시스템은 사상체질(태양·태음·소양·소음) 이론을 바탕으로 개인의 장부 허실과 기의 방향성을 분석합니다.
                </p>
                <p>
                  연령과 운동량 중심의 기존 추천 방식을 넘어, 타고난 생리적 특성과 실제 섭취 후의 신체 반응 데이터를 결합하여 ‘왜 이 단백질이 나에게 맞는지’에 대한 명확한 근거를 제시합니다.
                </p>
                <div className="p-6 bg-muted rounded-2xl border border-border italic text-sm">
                  ❗ 본 시스템은 진단이나 치료를 목적으로 하지 않는 비의료적 지원 도구이며, 한의원 진료를 대체하지 않습니다.
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.smooth}
              className="grid grid-cols-2 gap-4"
            >
              {constitutions.map((con: ConstitutionInfo) => (
                <ConstitutionCard key={con.id} constitution={con.id} />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">기술적 차별성</h2>
            <p className="text-muted-foreground">
              단순한 건강기능식품 추천을 넘어 데이터와 한방 원리가 결합된 체계적인 프로세스를 제공합니다.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              title="장부 허실 분석"
              description="간·폐·비·신 등 주요 장부의 상대적 균형 상태를 파악하여 기의 흐름을 분석합니다."
              icon={<Activity className="w-6 h-6 text-primary" />}
            />
            <FeatureCard 
              title="섭취 반응 피드백"
              description="제품 복용 후 소화 부담, 에너지 변화 등 실사용 데이터를 축적하여 최적의 섭취량을 조절합니다."
              icon={<Database className="w-6 h-6 text-primary" />}
            />
            <FeatureCard 
              title="한의원 연계 인프라"
              description="B2B 파트너십을 통해 진료실 내 치료가 일상 속 회복 관리로 이어지는 연속성을 확보합니다."
              icon={<Stethoscope className="w-6 h-6 text-primary" />}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">관리 프로세스</h2>
          </div>
          
          <div className="flex flex-col md:flex-row gap-8 items-start relative">
            <div className="flex-1 text-center space-y-4">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-primary/20">
                <ClipboardCheck className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold">01. 체질 분석</h3>
              <p className="text-muted-foreground">생활 패턴 및 신체 특성 질문을 통해 자신의 체질 경향성을 파악합니다.</p>
            </div>

            <div className="hidden md:block absolute top-8 left-1/3 w-12 h-px bg-border" />

            <div className="flex-1 text-center space-y-4">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-primary/20">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold">02. 맞춤 추천</h3>
              <p className="text-muted-foreground">지(地)·수(水)·화(火)·풍(風) 중 당신에게 가장 필요한 단백환을 제안합니다.</p>
            </div>

            <div className="hidden md:block absolute top-8 right-1/3 w-12 h-px bg-border" />

            <div className="flex-1 text-center space-y-4">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-primary/20">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold">03. 지속 관리</h3>
              <p className="text-muted-foreground">섭취 반응을 기록하고 근육량 및 컨디션 변화를 데이터로 추적 관리합니다.</p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <Link 
              to={ROUTE_PATHS.DIAGNOSIS}
              className="inline-flex items-center gap-2 text-primary font-bold hover:underline group"
            >
              지금 바로 첫 번째 단계를 시작하세요 <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.HEALTH_CONSULTATION_5} 
            alt="Consultation"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="max-w-3xl mx-auto p-12 bg-background/80 backdrop-blur-xl rounded-[2.5rem] border border-border shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              당신의 근육 건강,<br />
              체질로부터 답을 찾으세요.
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              한의원 진료 후 생활 관리의 연장선에서, <br />
              일상 속 전문적인 영양 관리를 경험해 보세요.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to={ROUTE_PATHS.DIAGNOSIS}
                className="px-10 py-4 bg-primary text-primary-foreground rounded-full font-bold hover:scale-[1.02] transition-all"
              >
                체질 진단 받기
              </Link>
              <Link 
                to={ROUTE_PATHS.PRODUCTS}
                className="px-10 py-4 bg-secondary text-secondary-foreground rounded-full font-bold hover:bg-secondary/80 transition-all"
              >
                제품 라인업 보기
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;